import { countPhrase } from '@/utils/wording-util/wordingUtil'

const SIBLING_OVERRIDES = { one: '{count} override at other scope', other: '{count} overrides at other scopes' }

/**
 * Microcopy used across the Configuration feature. Trimmed to keys
 * currently rendered — add new entries only when first consumed.
 */
export const CONFIGURATION_COPY = {
  page: {
    title: 'Configuration',
    titleSuffix: 'configuration',
    subtitle: 'Manage settings across your tenant, devices, and integrations.'
  },
  badges: {
    default: 'Default (built-in)',
    inherited: 'Inherited',
    setHere: 'Set here',
    notSet: 'Not set'
  },
  actions: {
    reset: 'Reset to inherited',
    copyKey: 'Copy key',
    showTrace: 'How is this resolved?',
    hideTrace: 'Hide resolution'
  },
  save: {
    saving: 'Saving…',
    saved: 'Saved'
  },
  landing: {
    welcomeTitle: 'Configuration',
    welcomeBody:
      'Manage settings that apply across your tenant, individual POS devices, payment methods, and more. Unchanged values use the built-in defaults.'
  },
  search: {
    placeholder: 'Filter settings…',
    noMatchInDomain: 'No settings match your search in this domain.'
  },
  empty: {
    domain: 'All values in this section use defaults from code.',
    landing: 'No configuration domains are registered yet.',
    allDefaultsTitle: 'All settings are using built-in defaults',
    allDefaultsHint: 'Tap a setting to override.',
    showAllDefaultsCta: 'Show all default settings'
  },
  header: {
    customisationSummary: 'customised',
    of: 'of'
  },
  tile: {
    settingSingular: 'setting',
    settingPlural: 'settings',
    customisedSuffix: 'customised',
    allDefaults: 'all defaults'
  },
  scope: {
    label: 'Scope:',
    backToTenant: 'Back to Tenant',
    loadingPlaceholder: 'Loading…',
    selectPlaceholder: 'Select…'
  },
  field: {
    placeholderForObjectDefault: 'Default value',
    placeholderDefaultPrefix: 'Default',
    copiedToastSummary: 'Copied',
    descriptionFallback: '—',
    defaultPrefix: 'Default',
    inheritedFromPrefix: 'Inherited from',
    notSetLabel: 'Not set',
    enumOptionsPrefix: 'One of',
    overflowMenuLabel: 'More actions',
    textLinesPlaceholder: 'One per line',
    secretSetPlaceholder: 'Saved. Type a new value to replace it.'
  },
  trace: {
    valueUsedPrefix: 'value used',
    noOverride: 'no override at this scope',
    noOverrideAtLevel: 'No overrides at this level',
    skippedPrefix: 'Skipped (override exists but a more-specific level won)',
    defaultUsedPrefix: 'default used',
    defaultWouldBePrefix: 'default would be',
    loadError: 'Could not load resolution trace',
    siblingOverridesPrefix: (n: number) => countPhrase(n, SIBLING_OVERRIDES),
    nullishPlaceholder: '∅',
    hiddenValue: 'hidden',
    unrepresentableFallback: '[object]'
  },
  pickList: {
    availableHeader: 'Available',
    selectedHeader: 'Selected'
  },
  readOnlyValue: {
    badge: 'Read-only',
    tooltip: 'The allowed values for this setting are not available to this page, so it cannot be edited here.'
  },
  readOnlyAtScope: {
    badge: 'Read-only here',
    tooltip: 'This setting cannot be overridden at the current scope. Switch scope to edit.'
  },
  mutations: {
    saveInit: {
      summary: 'Saving',
      detail: 'Saving configuration value…'
    },
    saveSuccess: {
      summary: 'Saved',
      detail: 'Configuration value saved.'
    },
    saveError: {
      summary: 'Save failed',
      detail: 'Configuration value could not be saved.'
    },
    resetInit: {
      summary: 'Resetting',
      detail: 'Resetting setting to inherited value…'
    },
    resetSuccess: {
      summary: 'Reset',
      detail: 'Setting reset to inherited value.'
    },
    resetError: {
      summary: 'Reset failed',
      detail: 'Setting could not be reset.'
    },
    loadError: {
      summary: 'Load failed',
      detail: 'Could not load configuration.'
    }
  },
  tree: {
    filterPlaceholder: 'Filter settings…',
    customisedBadgeAriaSuffix: 'customised',
    noMatchesTitle: 'No settings match',
    noMatchesHint: 'Try a different word, or clear the filter.',
    payments: 'Payments'
  },
  scopePill: {
    tenantPrefix: 'Editing',
    tenantLabel: 'Tenant',
    scopedSeparator: ' — '
  },
  scopeBanner: {
    tenantBody:
      'You are editing tenant-wide defaults. These values apply across every location and device unless customised for a specific scope.',
    scopedBodyTemplate:
      "You're editing settings for {entity} ({level}). Changes here apply only to this {level} — other {levelPlural} keep their current values."
  },
  scopeModal: {
    header: 'Switch scope',
    question: 'Where do you want to edit?',
    levelLegend: 'Level',
    entityLegend: 'Choose',
    entitySearchPlaceholder: 'Search…',
    noEntities: 'Nothing to choose from.',
    noEntityMatches: 'No matches.',
    cancel: 'Cancel',
    switch: 'Switch'
  },
  bulkReset: {
    cta: 'Reset all in this section to defaults',
    confirmHeader: 'Reset section',
    confirmBody: 'This will reset every customised value in this section to its default. This cannot be undone.',
    confirmAccept: 'Reset section'
  },
  fieldReset: {
    confirmHeader: 'Reset this setting',
    confirmBodyTemplate:
      "Clear your custom value for '{label}' and revert to the inherited value? This cannot be undone.",
    confirmAccept: 'Reset'
  }
} as const
